<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue';
import { RouterView, useRouter } from 'vue-router';

import { useErrorStore } from '@ohmybible/core';
import { UiToaster } from '@ohmybible/ui';

import ErrorScreen from '@/shared/components/ErrorScreen.vue';

const router = useRouter();
const errorStore = useErrorStore();

/**
 * Page transitions follow the navigation stack (`meta.depth`): going deeper
 * slides in from the end, going back from the start, a sideways move fades.
 */
type Direction = 'forward' | 'back' | 'none';

const direction = ref<Direction>('none');

const removeAfterEach = router.afterEach((to, from) => {
  const delta = (to.meta.depth ?? 0) - (from.meta.depth ?? 0);

  direction.value = delta > 0 ? 'forward' : (delta < 0 ? 'back' : 'none');
});

onBeforeUnmount(removeAfterEach);

const transition = computed(() => ({
  enterActiveClass: 'transition duration-200 ease-standard',
  leaveActiveClass: 'transition duration-150 ease-in',
  enterFromClass: {
    forward: 'opacity-0 translate-x-6 rtl:-translate-x-6',
    back: 'opacity-0 -translate-x-6 rtl:translate-x-6',
    none: 'opacity-0',
  }[direction.value],
  leaveToClass: {
    forward: 'opacity-0 -translate-x-6 rtl:translate-x-6',
    back: 'opacity-0 translate-x-6 rtl:-translate-x-6',
    none: 'opacity-0',
  }[direction.value],
}));
</script>

<template>
  <ErrorScreen
    v-if="errorStore.error"
    :title="errorStore.error.title"
    :description="errorStore.error.description"
  />
  <RouterView
    v-else
    v-slot="{ Component, route }"
  >
    <Transition
      v-bind="transition"
      mode="out-in"
    >
      <component
        :is="Component"
        :key="route.path"
      />
    </Transition>
  </RouterView>

  <UiToaster />
</template>
